import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Service } from "@/types/service";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, Star, Check, X, Folder } from "lucide-react";

interface DisplayOrderTableProps {
  services: Service[];
  regionCurrency: string;
  refetchServices: () => void;
}

export const DisplayOrderTable = ({ 
  services, 
  regionCurrency,
  refetchServices
}: DisplayOrderTableProps) => {
  const { toast } = useToast();
  const [editedServices, setEditedServices] = useState<Record<number, number>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [featuredServices, setFeaturedServices] = useState<Service[]>([]);

  useEffect(() => {
    if (services) {
      // Filter featured services and sort by display_order
      const featured = services
        .filter(service => service.featured)
        .sort((a, b) => {
          // Handle null display_order values (push them to the end)
          if (a.display_order === null && b.display_order === null) return 0;
          if (a.display_order === null) return 1;
          if (b.display_order === null) return -1;
          return a.display_order - b.display_order;
        });
      
      setFeaturedServices(featured);
    }
  }, [services]);

  const getCategoryDisplay = (service: Service) => {
    if (service.subcategory_details?.label) {
      const parentLabel = service.subcategory_details.parent?.label;
      if (parentLabel) {
        return `${parentLabel} - ${service.subcategory_details.label}`;
      }
      return service.subcategory_details.label;
    }
    return 'No category';
  };

  const handleDisplayOrderChange = (serviceId: number, value: string) => {
    const numericValue = parseInt(value, 10);
    
    // If the input is not a valid number, don't update the state
    if (isNaN(numericValue)) return;
    
    setEditedServices(prev => ({
      ...prev,
      [serviceId]: numericValue
    }));
  };

  const saveChanges = async () => {
    if (Object.keys(editedServices).length === 0) {
      toast({
        title: "No changes to save",
        description: "You haven't made any changes to the display order.",
        variant: "default",
      });
      return;
    }

    setIsSaving(true);

    try {
      // Create an array of promises for each service update
      const updatePromises = Object.entries(editedServices).map(([serviceIdStr, displayOrder]) => {
        // Convert the string key to a number
        const serviceId = parseInt(serviceIdStr, 10);
        
        return supabase
          .from('services')
          .update({ display_order: displayOrder })
          .eq('id', serviceId);
      });

      // Execute all updates in parallel
      const results = await Promise.all(updatePromises);
      
      // Check for any errors
      const errors = results.filter(result => result.error);
      
      if (errors.length > 0) {
        console.error("Errors updating display order:", errors);
        toast({
          title: "Error updating display order",
          description: "Some changes could not be saved. Please try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Changes saved",
          description: "Display order has been updated successfully.",
          variant: "success",
        });
        
        // Reset edited services
        setEditedServices({});
        
        // Refresh the services list
        refetchServices();
      }
    } catch (error) {
      console.error("Error saving display order:", error);
      toast({
        title: "Error saving changes",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (featuredServices.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900">No Featured Services</h3>
        <p className="mt-2 text-sm text-gray-500">
          To manage display order, first set services as featured in the service editor.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Featured Services Display Order</h2>
        <Button 
          onClick={saveChanges} 
          disabled={isSaving || Object.keys(editedServices).length === 0}
        >
          <Save className="w-4 h-4 mr-2" />
          Save All Changes
        </Button>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>Service Name</TableHead>
              <TableHead>Display Order</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Published</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {featuredServices.map((service) => (
              <TableRow key={service.id} className="hover:bg-muted/50">
                <TableCell>
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                </TableCell>
                <TableCell className="font-medium">{service.name}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    className="w-24"
                    value={
                      service.id in editedServices
                        ? editedServices[service.id]
                        : service.display_order || ''
                    }
                    onChange={(e) => handleDisplayOrderChange(service.id, e.target.value)}
                    min={1}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Folder className="h-4 w-4 mr-2 text-gray-500" />
                    <span>{getCategoryDisplay(service)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {service.published ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <X className="h-4 w-4 text-red-500" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-md text-sm text-blue-800">
        <p>
          <strong>Note:</strong> Lower display order numbers appear first. Services with the same display order 
          are then sorted by creation date.
        </p>
      </div>
    </div>
  );
};
